import http from 'http';
import { readFile, writeFile } from 'fs/promises';
import path from 'path';
import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import { WorkflowAPI } from '@gitlab-lsp/workflow-api';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import {
  ConfigService,
  IConfig,
  WORKFLOW_DEFAULT_EXECUTOR_VERSION,
  WORKFLOW_DEFAULT_EXECUTOR_DOWNLOAD_PATH,
} from '../../common';
import { LsFetch } from '../../common/fetch';
import { log } from '../../common/log';
import { GitLabApiClient } from '../../common/api';
import {
  WorkflowEventsChannel,
  WorkflowEventsType,
} from './api/graphql/worfklow_events_response_channel';

export interface GenerateTokenResponse {
  gitlab_rails: {
    base_url: string;
    token: string;
  };
  duo_workflow_executor: {
    executor_binary_url: string;
    version: string;
  };
  duo_workflow_service: {
    base_url: string;
    token: string;
    secure: boolean;
    headers: {
      'X-Gitlab-Host-Name': string;
      'X-Gitlab-Instance-Id': string;
      'X-Gitlab-Realm': string;
      'X-Gitlab-Version': string;
      'X-Gitlab-Global-User-Id': string;
    };
  };
}

interface CreateWorkflowResponse {
  id: string;
}

export enum WorkflowStatus {
  NOT_STARTED = 'Not Started',
  PLANNING = 'Planning',
  EXECUTION = 'Execution',
  COMPLETED = 'Completed',
  ERROR = 'Error',
}

const DuoWorkflowAPIService = createInterfaceId<WorkflowAPI>('WorkflowAPI');

@Injectable(DuoWorkflowAPIService, [ConfigService, LsFetch, GitLabApiClient])
export class DesktopWorkflowRunner implements WorkflowAPI {
  #dockerSocket?: string;

  #folders?: WorkspaceFolder[];

  #fetch: LsFetch;

  #api: GitLabApiClient;

  #projectPath: string | undefined;

  #logLevel: string | undefined;

  constructor(configService: ConfigService, fetch: LsFetch, api: GitLabApiClient) {
    this.#fetch = fetch;
    this.#api = api;
    configService.onConfigChange((config) => this.#reconfigure(config));
  }

  #reconfigure(config: IConfig) {
    this.#dockerSocket = config.client.duoWorkflowSettings?.dockerSocket;
    this.#folders = config.client.workspaceFolders || [];
    this.#projectPath = config.client.projectPath;
    this.#logLevel = config.client.logLevel;
  }

  async subscribeToUpdates(
    messageCallback: (message: WorkflowEventsType, status: WorkflowStatus) => Promise<void>,
    workflowId: string,
  ) {
    const channel = new WorkflowEventsChannel({
      workflowId: `gid://gitlab/Ai::DuoWorkflows::Workflow/${workflowId}`,
    });

    const cable = await this.#api.connectToCable();

    channel.on('checkpoint', async (msg) => {
      const status = this.#getStatus(msg);
      if (status === WorkflowStatus.COMPLETED) {
        cable.disconnect();
      }
      await messageCallback(msg, status);
    });

    cable.subscribe(channel);
  }

  #getStatus(workflowEvent?: WorkflowEventsType) {
    if (workflowEvent?.checkpoint) {
      try {
        const parsedCheckpoint = JSON.parse(workflowEvent.checkpoint);
        return parsedCheckpoint?.channel_values?.status || WorkflowStatus.NOT_STARTED;
      } catch {
        return WorkflowStatus.NOT_STARTED;
      }
    }

    return WorkflowStatus.NOT_STARTED;
  }

  async runWorkflow(goal: string, image: string): Promise<string> {
    if (!this.#dockerSocket) {
      throw new Error('Docker socket not configured');
    }

    if (!this.#folders || this.#folders.length === 0) {
      throw new Error('No workspace folders');
    }

    const folderName = this.#folders[0].uri.replace(/^file:\/\//, '');

    if (this.#folders.length > 0) {
      log.info(`More than one workspace folder detected. Using workspace folder ${folderName}`);
    }

    const workflowId = await this.#createWorkflow();
    const workflowToken = await this.#getWorkflowToken();

    const executorVersion =
      workflowToken?.duo_workflow_executor?.version || WORKFLOW_DEFAULT_EXECUTOR_VERSION;
    const executorPath = path.join(
      __dirname,
      `../vendor/duo-workflow-executor-${executorVersion}.tar.gz`,
    );

    await this.#downloadWorkflowExecutor(
      workflowToken.duo_workflow_executor.executor_binary_url,
      executorPath,
    );

    const containerOptions = {
      Image: image,
      Cmd: this.executorCommand(workflowId, workflowToken, goal, this.#logLevel),
      HostConfig: {
        Binds: [`${folderName}:/workspace`],
      },
    };

    // Create a container
    const createResponse = JSON.parse(
      await this.#makeRequest('/v1.43/containers/create', 'POST', JSON.stringify(containerOptions)),
    );

    // Check if Id in createResponse
    if (!createResponse.Id) {
      throw new Error('Failed to create container: No Id in response');
    }

    const containerID = createResponse.Id;

    log.info(`Created the docker container: ${containerID}`);

    // Copy the executor into the container
    await this.#copyExecutor(containerID, executorPath);

    // Start the container
    await this.#makeRequest(`/v1.43/containers/${containerID}/start`, 'POST', '');
    log.info(`Started the docker container: ${containerID}`);

    return workflowId;
  }

  async #copyExecutor(containerID: string, executorPath: string) {
    const fileContents = await readFile(executorPath);
    await this.#makeRequest(
      `/v1.43/containers/${containerID}/archive?path=/`,
      'PUT',
      fileContents,
      'application/x-tar',
    );
  }

  #makeRequest(
    apiPath: string,
    method: string,
    data: string | Buffer,
    contentType = 'application/json',
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.#dockerSocket) {
        throw new Error('Docker socket not set');
      }

      const options: http.RequestOptions = {
        socketPath: this.#dockerSocket,
        path: apiPath,
        method,
        headers: {
          'Content-Type': contentType,
          'Content-Length': Buffer.byteLength(data),
        },
      };

      const callback = (res: http.IncomingMessage) => {
        res.setEncoding('utf-8');
        res.on('data', (content) => resolve(content));
        res.on('error', (err) => reject(err));
        res.on('end', () => {
          resolve('');
        });
      };

      const clientRequest = http.request(options, callback);
      clientRequest.write(data);
      clientRequest.end();
    });
  }

  async #downloadWorkflowExecutor(executorBinaryUrl: string, executorPath: string): Promise<void> {
    try {
      await readFile(executorPath);
    } catch (error) {
      log.info('Downloading workflow executor...');
      const fileUrl = executorBinaryUrl || WORKFLOW_DEFAULT_EXECUTOR_DOWNLOAD_PATH;
      await this.#downloadFile(fileUrl, executorPath);
    }
  }

  async #downloadFile(url: string, outputPath: string): Promise<void> {
    const response = await this.#fetch.fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const buffer = await response.arrayBuffer();
    await writeFile(outputPath, Buffer.from(buffer));
  }

  async #createWorkflow(): Promise<string> {
    const response = await this.#api?.fetchFromApi<CreateWorkflowResponse>({
      type: 'rest',
      method: 'POST',
      path: '/ai/duo_workflows/workflows',
      body: {
        project_id: this.#projectPath,
      },
      supportedSinceInstanceVersion: {
        resourceName: 'create a workflow',
        version: '17.3.0',
      },
    });

    if (!response) {
      throw new Error('Failed to create workflow');
    }

    return response.id;
  }

  async #getWorkflowToken(): Promise<GenerateTokenResponse> {
    const token = await this.#api?.fetchFromApi<GenerateTokenResponse>({
      type: 'rest',
      method: 'POST',
      path: '/ai/duo_workflows/direct_access',
      supportedSinceInstanceVersion: {
        resourceName: 'get workflow direct access',
        version: '17.3.0',
      },
    });

    if (!token) {
      throw new Error('Failed to get workflow token');
    }

    return token;
  }

  executorCommand(
    workflowId: string,
    workflowToken: GenerateTokenResponse,
    goal: string,
    logLevel: string | undefined,
  ) {
    let cmd = [
      '/duo-workflow-executor',
      `--workflow-id=${workflowId}`,
      '--server',
      workflowToken.duo_workflow_service.base_url || 'localhost:50052',
      '--goal',
      goal,
      '--base-url',
      workflowToken.gitlab_rails.base_url,
      '--token',
      workflowToken.gitlab_rails.token,
      '--duo-workflow-service-token',
      workflowToken.duo_workflow_service.token,
      '--realm',
      workflowToken.duo_workflow_service.headers['X-Gitlab-Realm'],
      '--user-id',
      workflowToken.duo_workflow_service.headers['X-Gitlab-Global-User-Id'],
      '--git-http-base-url',
      workflowToken.gitlab_rails.base_url,
      '--git-http-user',
      'auth',
      '--git-password',
      workflowToken.gitlab_rails.token,
      '--ignore-git-dir-owners',
    ];

    if (!workflowToken.duo_workflow_service.secure) {
      cmd = [...cmd, '--insecure'];
    }

    if (logLevel === 'debug') {
      cmd = [...cmd, '--debug'];
    }

    return cmd;
  }
}
